import React, { useCallback, useEffect, useState, useMemo } from "react";
import PlusIcon from "@styles/all-images/icons/plus.svg";
import CustomTable from "@/components/common/table/CustomTable";
import dateHandler from "@/composables/dateUtil";
import { toast } from "react-toastify";
import actionsHandler from "@/store/actions/actionHandler";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import ShareIcon from "@/styles/all-images/icons/send.svg";
import EyeIcon from "@styles/all-images/icons/view-icon.svg";
import AddClient from "@/pages/ClientOnBoarding/AddClient";
import UserIcon from "@styles/all-images/icons/bg-user.svg";
import { useNavigate } from "react-router-dom";
import CheckBlueIcon from "@styles/all-images/icons/checkblue.svg";
import CloseBlueIcon from "@styles/all-images/icons/closeblue.svg";
import TierConfigurationRejectionReason from "@pages/TierConfiguration/TierConfigurationRejectionReason";
import ViewRejectionReason from "@/pages/SupplierOnBoarding/ViewRejectionReason";
import { usePermissionState } from "@/providers/PermissionProvider";
import {
  BasePermissions,
  ClientOnboardingPermissions,
} from "@/constants/permission-modules/base-permissions";

export type MasterType =
  | "invitedRequest"
  | "pendingRequest"
  | "rejectedRequest";

export interface MasterDataTableProps {
  type: MasterType;
  fetchData: (
    type: MasterType,
    params: { limit?: number; page?: number; search?: string },
    isArchiveView: boolean,
  ) => Promise<any>;
}

const MasterDataTable: React.FC<MasterDataTableProps> = ({
  type,
  fetchData,
}) => {
  const navigate = useNavigate();

  const dispatch = useDispatch<AppThunkDispatch>();
  const { formatDate } = dateHandler();
  const [forceRefresh, setForceRefresh] = useState(false);
  const [forceReset, setForceReset] = useState(false);
  const [isArchiveView, setIsArchiveView] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [columns, setColumns] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const [isViewRejectionModalOpen, setIsViewRejectionModalOpen] =
    useState(false);

  const { isPermitted } = usePermissionState();
  const titleNameObject = useMemo(
    () => ({
      invitedRequest: {
        title: "invitedRequest",
        display: "Invited Request",
      },
      pendingRequest: {
        title: "pendingRequest",
        display: "Pending Request",
      },
      rejectedRequest: {
        title: "rejectedRequest",
        display: "Rejected Request",
      },
    }),
    [],
  );

  const handleFetch = useCallback(
    async (params: {
      page: number;
      limit: number;
      search?: string;
      sortColumn?: string;
      sortDirection?: "asc" | "desc";
    }) => {
      return fetchData(type, params, isArchiveView);
    },
    [type, isArchiveView, fetchData],
  );

  const handleView = (row: any) => {
    navigate(`/user-management-details/${row.id}`);
  };

  // const handleCloseProfileModal = (type: "close" | "submit") => {
  //   setIsProfileModalOpen(false);
  //   if (type === "submit") {
  //     setForceRefresh(true);
  //   }
  // };

  const handleReject = (row: any) => {
    setSelectedItem(row);
    setIsRejectionModalOpen(true);
  };

  const handleRejectSubmit = async (rejectionReason: string) => {
    try {
      setIsLoading(true);
      const { authenticatedCall } = actionsHandler("userProfileApprovalStatus");
      const response = await dispatch(
        authenticatedCall({
          method: "post",
          payload: {
            id: selectedItem.id,
            status: 2,
            rejection_reason: rejectionReason,
          },
        }),
      );

      if (response.ok) {
        toast.success(
          response.data?.message ||
            "Profile update request rejected successfully",
        );
        setIsRejectionModalOpen(false);
        setForceRefresh(true);
      }
    } catch (error) {
      console.error("Error rejecting profile update:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getActionColumn = (type: MasterType) => {
    switch (type) {
      case "invitedRequest":
        return isPermitted(ClientOnboardingPermissions.UPDATE_CLIENT_ONBOARDING)
          ? {
              header: "Resend",
              accessor: (row: any) => (
                <div className="tw-flex tw-items-center">
                  <button onClick={() => handleShare(row)} title="Resend">
                    <img
                      src={ShareIcon}
                      alt="Share"
                      className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                    />
                  </button>
                </div>
              ),
            }
          : null;
      case "pendingRequest":
        return isPermitted(
          ClientOnboardingPermissions.APPROVE_REJECT_CLIENT_ONBOARDING,
        )
          ? {
              header: "Actions",
              accessor: (row: any) => (
                <div className="tw-flex tw-items-center">
                  <button onClick={() => handleApprove(row)} title="Approve">
                    <img
                      src={CheckBlueIcon}
                      alt="Approve"
                      className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                    />
                  </button>
                  <button onClick={() => handleReject(row)} title="Reject">
                    <img
                      src={CloseBlueIcon}
                      alt="Reject"
                      className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                    />
                  </button>
                </div>
              ),
            }
          : null;
      case "rejectedRequest":
        return {
          header: "Actions",
          accessor: (row: any) => (
            <div className="tw-flex tw-items-center">
              <button
                onClick={() => {
                  setSelectedItem(row);
                  setIsViewRejectionModalOpen(true);
                }}
                title="View"
              >
                <img
                  src={EyeIcon}
                  alt="View"
                  className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                />
              </button>
              {isPermitted(
                ClientOnboardingPermissions.UPDATE_CLIENT_ONBOARDING,
              ) && (
                <button onClick={() => handleShare(row)} title="Resend">
                  <img
                    src={ShareIcon}
                    alt="Share"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>
              )}
            </div>
          ),
        };
      default:
        return {
          header: "Action",
          accessor: (row: any) => (
            <div className="tw-flex tw-items-center">
              <button
                onClick={() => setIsViewRejectionModalOpen(true)}
                title="View"
              >
                <img
                  src={EyeIcon}
                  alt="View"
                  className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                />
              </button>
              <button onClick={() => handleShare(row)} title="Resend">
                <img
                  src={ShareIcon}
                  alt="Share"
                  className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                />
              </button>
            </div>
          ),
        };
    }
  };

  const handleApprove = async (row: any) => {
    try {
      setIsLoading(true);
      const { authenticatedCall } = actionsHandler("userProfileApprovalStatus");
      const response = await dispatch(
        authenticatedCall({
          method: "post",
          payload: {
            id: row.id,
            status: 1,
            rejection_reason: null,
          },
        }),
      );

      if (response.ok) {
        toast.success(
          response.data?.message ||
            "Profile update request approved successfully",
        );
        setForceRefresh(true);
      }
    } catch (error) {
      console.error("Error approving profile update:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleShare = async (row: any) => {
    try {
      setIsLoading(true);
      const { authenticatedCall } = actionsHandler("invitationResend");

      const response = await dispatch(
        authenticatedCall({
          payload: {
            email: row.email,
          },
        }),
      );

      if (response.ok) {
        toast.success(response.data?.message || "Invitation sent successfully");
        setForceRefresh(true);
      }
    } catch (error) {
      console.error("Error sending invitation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSettingColumns = useCallback(() => {
    const columnConfigs = {
      invitedRequest: [
        {
          header: "Email Address",
          accessor: "email",
          sortable: true,
          sortKey: "email",
        },
        {
          header: "Invited On",
          accessor: (row: any) => formatDate(row.created_at),
          sortable: true,
          sortKey: "created_at",
        },
      ],
      pendingRequest: [
        {
          header: "Client Name",
          accessor: (row: any) => (
            <div className="tw-flex tw-items-center tw-gap-2">
              <img src={row.profile_picture || UserIcon} alt="User Icon" />
              <span>{row.name}</span>
            </div>
          ),
          sortable: true,
          sortKey: "name",
        },
        {
          header: "Email Address",
          accessor: "email",
          sortable: true,
          sortKey: "email",
        },
        {
          header: "Contact Number",
          accessor: "phone",
          sortable: true,
          sortKey: "phone",
        },
        {
          header: "Requested On",
          accessor: (row: any) => formatDate(row.created_at),
          sortable: true,
          sortKey: "user_detail__created_at",
        },
      ],
      rejectedRequest: [
        {
          header: "Client Name",
          accessor: (info: any) => (
            <div className="tw-flex tw-items-center tw-gap-2">
              <img src={info.profile_picture || UserIcon} alt="User Icon" />
              <span>{info.name}</span>
            </div>
          ),
          sortable: true,
          sortKey: "name",
        },
        {
          header: "Email Address",
          accessor: "email",
          sortable: true,
          sortKey: "email",
        },
        {
          header: "Contact Number",
          accessor: "phone",
          sortable: true,
          sortKey: "phone",
        },
        {
          header: "Requested On",
          accessor: (row: any) => formatDate(row.created_at),
          sortable: true,
          sortKey: "created_at",
        },
        {
          header: "Rejected On",
          accessor: (row: any) => formatDate(row.updated_at),
          sortable: true,
          sortKey: "user_detail__updated_at",
        },
      ],
    };

    const baseColumns = columnConfigs[type];
    const actionColumn = getActionColumn(type);
    setColumns(actionColumn ? [...baseColumns, actionColumn] : baseColumns);
  }, [type]);

  useEffect(() => {
    handleSettingColumns();
    setForceReset(true);
  }, [isArchiveView]);

  const getTableButtons = (type: MasterType) => {
    switch (type) {
      case "invitedRequest":
        return isPermitted(ClientOnboardingPermissions.CREATE_CLIENT_ONBOARDING)
          ? {
              addButton: {
                text: "Add Client",
                icon: PlusIcon,
                onClick: () => setModalOpen(true),
              },
            }
          : {};
      default:
        return {};
    }
  };

  const getFilterOptions = (type: MasterType) => {
    return {
      show: true,
      placeholder: "Filter by Status",
      options: [
        { id: "active", name: "Active" },
        { id: "inactive", name: "Inactive" },
        // Add more filter options as needed
      ],
      onFilterChange: (selectedFilters: any[]) => {
        // Handle filter change
        console.log("Selected filters:", selectedFilters);
        setForceRefresh(true);
      },
    };
  };

  return (
    <>
      <CustomTable
        columns={columns}
        fetchData={handleFetch}
        forceRefresh={forceRefresh}
        setForceRefresh={setForceRefresh}
        forceReset={forceReset}
        setForceReset={setForceReset}
        addButton={getTableButtons(type).addButton}
        noDataMessage={`No ${titleNameObject[type].display} found`}
        noSearchDataMessage={`No matching ${titleNameObject[type].display} found`}
      />

      <AddClient
        onClose={() => setModalOpen(false)}
        onSubmit={() => {
          setForceRefresh(true);
          setModalOpen(false);
        }}
        isModalOpen={modalOpen}
      />

      {/* {isProfileModalOpen && (
        <UserManagementEditCard
          onClose={() => handleCloseProfileModal("close")}
          onSubmit={() => handleCloseProfileModal("submit")}
          staffId={selectedProfile.id}
          selectedProfiles={
            selectedProfile.permission_profiles?.map(
              (profile: any) => profile.id,
            ) || []
          }
        />
      )} */}

      {isRejectionModalOpen && (
        <TierConfigurationRejectionReason
          onClose={() => setIsRejectionModalOpen(false)}
          onSubmit={handleRejectSubmit}
          isLoading={isLoading}
        />
      )}
      {isViewRejectionModalOpen && selectedItem && (
        <ViewRejectionReason
          onClose={() => setIsViewRejectionModalOpen(false)}
          rejectionReason={selectedItem.rejection_reason}
          onSubmit={() => {}}
        />
      )}
    </>
  );
};

export default MasterDataTable;
